import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin } from "lucide-react";
import { LOGO_LOCKUP, SITE, FOOTER_NAV } from "@/lib/siteData";
import { TrustindexCertificate } from "@/components/site/TrustindexReviews";

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-background">
      <div className="container-site py-14 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block">
              <img src={LOGO_LOCKUP} alt="Pie in the Sky Productions" className="h-24 w-auto" />
            </Link>
            <p className="mt-5 text-sm text-muted-foreground max-w-md leading-relaxed">
              A Cape Town film & video agency producing cinematic brand stories for clients across South Africa, the UK, Europe and the Middle East.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a data-testid="footer-instagram" href={SITE.socials.instagram} target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 hover:bg-card hover:border-[hsl(var(--primary))]/60 transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a data-testid="footer-youtube" href={SITE.socials.youtube} target="_blank" rel="noreferrer noopener" aria-label="YouTube" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 hover:bg-card hover:border-[hsl(var(--primary))]/60 transition-colors">
                <Youtube className="h-4 w-4" />
              </a>
              <a data-testid="footer-linkedin" href={SITE.socials.linkedin} target="_blank" rel="noreferrer noopener" aria-label="LinkedIn" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 hover:bg-card hover:border-[hsl(var(--primary))]/60 transition-colors">
                <Linkedin className="h-4 w-4" />
              </a>
              <a data-testid="footer-gmb" href={SITE.gmb.searchUrl} target="_blank" rel="noreferrer noopener" aria-label="Google Business Profile" className="inline-flex h-10 px-4 items-center justify-center rounded-full border border-border bg-card/60 hover:bg-card hover:border-[hsl(var(--primary))]/60 transition-colors text-xs uppercase tracking-[0.16em]">
                View on Google
              </a>
            </div>
          </div>

          {Object.entries(FOOTER_NAV).map(([heading, items]) => (
            <div key={heading} className="lg:col-span-2">
              <p className="kicker mb-4">{heading}</p>
              <ul className="space-y-3">
                {items.map((n) => (
                  <li key={n.href}>
                    <Link data-testid={`footer-${heading.toLowerCase()}-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} to={n.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <p className="kicker mb-4">Contact</p>
            <ul className="space-y-3 text-sm">
              <li className="text-muted-foreground">{SITE.location}</li>
              <li><a href={`mailto:${SITE.email}`} className="text-foreground hover:text-[hsl(var(--primary))] transition-colors break-all">{SITE.email}</a></li>
              <li><a href={`tel:${SITE.phone.replace(/\s+/g, "")}`} className="text-muted-foreground hover:text-foreground transition-colors">{SITE.phone}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-border flex flex-col gap-4">
          {/* Trustindex certificate - compact review credibility badge */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-2">
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/80 shrink-0">
              Verified reviews
            </p>
            <TrustindexCertificate
              className="min-h-[42px] w-full sm:w-auto flex items-center"
              testId="footer-trustindex-cert"
            />
          </div>
          <p className="text-[11px] text-muted-foreground/80 max-w-3xl leading-relaxed" data-testid="footer-bakery-disambiguation">
            Pie in the Sky Productions is a Cape Town film &amp; video production company. Looking for the bakery? Visit{" "}
            <a href="https://pie-in-the-sky.co.za" target="_blank" rel="noreferrer noopener" className="underline underline-offset-4 hover:text-foreground transition-colors">pie-in-the-sky.co.za</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">&copy; {year} {SITE.brand}. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <Link data-testid="footer-legal-privacy" to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link data-testid="footer-legal-terms" to="/working-agreement" className="text-muted-foreground hover:text-foreground transition-colors">Working Agreement</Link>
              <span className="text-muted-foreground font-mono-credits">{SITE.domain} &middot; Crafted in Cape Town.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
